import React, { useEffect, useRef } from 'react';
import {
  HeaderStyledSubNav,
  HeaderStyledSubNavButton,
  HeaderStyledSubNavList,
  HeaderStyledSubNavListContainer,
  HeaderStyledSubNavTeaserWrapper,
} from './AudiHeaderSubNavStyles';
import AudiHeaderSubNavListItem from './AudiHeaderSubNavListItem';
import type { AudiHeaderSubNavProps } from '../../interfaces/header-components.interfaces';
import AudiHeaderTeaser from '../AudiHeaderTeaser/AudiHeaderTeaser';
import { scriptRegex } from '../../utils/string-utils';
import { getKeyboardFocusableElements, keyTypes, navigate, trapFocus } from '../../utils/a11y';
import useKeyboard from '../../hooks/use-keyboars-events';
import { SUB_NAVIGATION_CLASSNAME } from '../../utils/globals';

const AudiHeaderSubNav: React.FC<AudiHeaderSubNavProps> = ({
  mainCategoryPosition,
  mainCategoryLabel,
  closeSubNavigation,
  onItemClick,
  headerNavigationItemsAmount,
  isSubNavOpen,
  subNavItem,
  useFootnoteReferenceServiceTextParserHook,
  useOneLayer,
}) => {
  const refContainer = useRef<HTMLDivElement>(null);
  const dangerousHTML = useFootnoteReferenceServiceTextParserHook(
    subNavItem?.Link?.Text?.replace(scriptRegex, ' illegal script '),
  );
  const [focusableElements, setFocusableElements] = React.useState<HTMLElement[]>([]);

  useEffect(() => {
    if (isSubNavOpen) {
      const elem = document.getElementsByClassName('submenu-open')[0] as HTMLDivElement;
      const focusedElements = getKeyboardFocusableElements(elem);
      setFocusableElements(focusedElements);
      // set initial focus on first element
      requestAnimationFrame(() => {
        focusedElements[0]?.focus();
      });
    }
  }, [isSubNavOpen]);

  useKeyboard(
    'keydown',
    (event) => {
      if (isSubNavOpen) {
        if (refContainer?.current && focusableElements.length > 0) {
          trapFocus(focusableElements, event);
          if (event.key === keyTypes.ArrowLeft || event.key === keyTypes.ArrowRight) {
            navigate(
              refContainer.current,
              event.key,
              keyTypes.ArrowRight,
              keyTypes.ArrowLeft,
              `.${SUB_NAVIGATION_CLASSNAME}`,
            );
          }
        }
        if (event.key === keyTypes.ArrowUp) {
          closeSubNavigation(event as unknown as React.MouseEvent<HTMLButtonElement>);
        }
      }
    },
    refContainer,
  );

  return (
    <HeaderStyledSubNav
      headerNavigationItemsAmount={headerNavigationItemsAmount}
      isSubNavOpen={isSubNavOpen}
      role="tabpanel"
      className={isSubNavOpen ? 'submenu-open' : ''}
    >
      <div>
        <HeaderStyledSubNavButton
          headerNavigationItemsAmount={headerNavigationItemsAmount}
          onClick={closeSubNavigation}
        >
          <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 16.9L15.5 11.5 10 6.1" fill="none" stroke="currentColor" />
          </svg>
          <span
            dangerouslySetInnerHTML={{
              __html: dangerousHTML,
            }}
          />
        </HeaderStyledSubNavButton>
        <HeaderStyledSubNavTeaserWrapper
          headerNavigationItemsAmount={headerNavigationItemsAmount}
          ref={refContainer}
        >
          <HeaderStyledSubNavListContainer
            aria-label={subNavItem.Link?.Text}
            headerNavigationItemsAmount={headerNavigationItemsAmount}
          >
            <HeaderStyledSubNavList headerNavigationItemsAmount={headerNavigationItemsAmount}>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              {subNavItem.SubNavigation.map((link, i) => {
                const key = `${link?.Text}${link?.Url}`;
                return (
                  <AudiHeaderSubNavListItem
                    index={i}
                    isSubNavOpen={isSubNavOpen}
                    key={key}
                    listItem={link}
                    onItemClick={onItemClick}
                    useFootnoteReferenceServiceTextParserHook={
                      useFootnoteReferenceServiceTextParserHook
                    }
                    useOneLayer={useOneLayer}
                  />
                );
              })}
            </HeaderStyledSubNavList>
          </HeaderStyledSubNavListContainer>
          {subNavItem?.Teaser && (
            <AudiHeaderTeaser
              isSubNavOpen={isSubNavOpen}
              teaser={subNavItem.Teaser}
              globalPosition={`${mainCategoryPosition + 1}-${
                subNavItem?.SubNavigation?.length ? subNavItem.SubNavigation.length + 1 : 1
              }`}
              mainCategoryLabel={mainCategoryLabel}
              useFootnoteReferenceServiceTextParserHook={useFootnoteReferenceServiceTextParserHook}
            />
          )}
        </HeaderStyledSubNavTeaserWrapper>
      </div>
    </HeaderStyledSubNav>
  );
};
export default AudiHeaderSubNav;
